import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CloudDownload from "@mui/icons-material/CloudDownload";
import EditIcon from "@mui/icons-material/Edit";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  uploadIDUConfig,
  updateIDUConfigByModelId,
  updateIDUConfigByConfigId,
  getIDUConfigData,
  downloadIDUConfigFile,
  getDeviceModelNumbersList,
  sendIDUConfigMQTTTrigger,
  getDeviceModelIdsUsingConfigId,
} from "../../../action/iduConfig";
import { setAlert } from "../../../action/alert";
import Alert from "../../layout/Alert";
import ResponsiveContentWrapper from "../../layout/ResponsiveContentWrapper";
import AppTabs from "../../Common/Tabs/AppTabs";
import FormInput from "../../Common/FormInputs/MuiTextField";
import AppCheckbox from "../../Common/FormInputs/AppCheckbox";
import AppButton from "../../Common/Button/Button";
import DataTable from "../../Common/DataTable/DataTable";
import AppRadioGroup from "../../Common/FormInputs/AppRadioGroup";
import { LabelText } from "../../Common/Typography/AppTypography";
import { selectMenuProps } from "../../../utils/selectMenuProps";
import { useDesignTokens } from "../../../theme/useDesignTokens";

const IDU_TABS = [
  { label: "View IDU EEPROM Config" },
  { label: "Upload IDU EEPROM Config" },
  { label: "Push Device IDU Config" },
];

const TABLE_HEADERS = {
  modelId: "Model ID",
  configId: "Config ID",
  iduConfigVersion: "Config Version",
  iduConfigFileObjectBin: "Config File",
  editIDUConfig: "Edit",
};

const formControlSx = {
  width: "100%",
  maxWidth: { xs: "100%", md: 360 },
};

const IDUEEPROMConfig = ({
  wrapperClass,
  allowedDeviceCategories,
  allowedCompanyBrand,
  iduConfigStore,
  uploadIDUConfig,
  downloadIDUConfigFile,
  sendIDUConfigMQTTTrigger,
  updateIDUConfigByModelId,
  updateIDUConfigByConfigId,
  setAlert,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [uploadedIDUConfigData, setUploadedIDUConfigData] = useState({});
  const [editIduConfig, setEditIDUConfig] = useState(false);

  useEffect(() => {
    if (activeTab !== 1) {
      setEditIDUConfig(false);
      setUploadedIDUConfigData({});
    }
  }, [activeTab]);

  const setIDUConfigDataForEdit = (iduConfig) => {
    setUploadedIDUConfigData(iduConfig);
    setEditIDUConfig(true);
    setActiveTab(1);
  };

  const handleCancelEditIduConfig = () => {
    setActiveTab(0);
    setEditIDUConfig(false);
    setUploadedIDUConfigData({});
  };

  return (
    <ResponsiveContentWrapper className={wrapperClass || ""}>
      <Alert />
      <Box className="idu-eeprom-config" sx={{ width: "100%", minWidth: 0 }}>
        <AppTabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          tabs={IDU_TABS}
          variant="scrollable"
          scrollButtons="auto"
          tabMinWidth="auto"
          appearance="neutral"
        />

        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <ViewIDUEEPROMConfig
              allowedDeviceCategories={allowedDeviceCategories}
              allowedCompanyBrand={allowedCompanyBrand}
              downloadIDUConfigFile={downloadIDUConfigFile}
              setAlert={setAlert}
              setIDUConfigDataForEdit={setIDUConfigDataForEdit}
              iduConfigStore={iduConfigStore}
            />
          )}

          {activeTab === 1 && (
            <UploadIDUEEPROMConfig
              allowedDeviceCategories={allowedDeviceCategories}
              allowedCompanyBrand={allowedCompanyBrand}
              iduConfigStore={iduConfigStore}
              uploadedIDUConfigData={uploadedIDUConfigData}
              editIDUConfig={editIduConfig}
              uploadIDUConfig={uploadIDUConfig}
              updateIDUConfigByModelId={updateIDUConfigByModelId}
              updateIDUConfigByConfigId={updateIDUConfigByConfigId}
              cancelEditIDUConfig={handleCancelEditIduConfig}
              setAlert={setAlert}
            />
          )}

          {activeTab === 2 && (
            <MQTTTriggerIDUEEPROMConfig
              iduConfigStore={iduConfigStore}
              sendIDUConfigMQTTTrigger={sendIDUConfigMQTTTrigger}
              setAlert={setAlert}
            />
          )}
        </Box>
      </Box>
    </ResponsiveContentWrapper>
  );
};

const ViewIDUEEPROMConfig = ({
  allowedDeviceCategories,
  allowedCompanyBrand,
  downloadIDUConfigFile,
  setAlert,
  setIDUConfigDataForEdit,
  iduConfigStore: { uploadIDUConfigInProgress, uploadIDUConfigSuccess },
}) => {
  const [categoryFilter, setCategoryFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [modelNumberFilterList, setModelNumberFilterList] = useState([]);
  const [modelNumberFilter, setModelNumberFilter] = useState("");
  const [configIdFilter, setConfigIdFilter] = useState("");
  const [iduConfigData, setIDUConfigData] = useState([]);
  const [showIDUConfigDataList, setShowIDUConfigDataList] = useState(false);
  const [isIDUConfigDataLoading, setIsIDUConfigDataLoading] = useState(false);
  const [updateFlag, setUpdateFlag] = useState(undefined);
  const [searchBy, setSearchBy] = useState("searchByModelId");

  const { colors } = useDesignTokens();

  useEffect(() => {
    if (!uploadIDUConfigInProgress && uploadIDUConfigSuccess) {
      setShowIDUConfigDataList(false);
    }
  }, [uploadIDUConfigInProgress, uploadIDUConfigSuccess]);

  useEffect(() => {
    if (!categoryFilter || !brandFilter) {
      return;
    }

    getDeviceModelNumbersList(brandFilter, categoryFilter)
      .then((data) => {
        setModelNumberFilterList(data || []);
        setShowIDUConfigDataList(false);
        setModelNumberFilter("");
      })
      .catch(() => {
        setModelNumberFilterList([]);
        setShowIDUConfigDataList(false);
        setModelNumberFilter("");
      });
  }, [categoryFilter, brandFilter]);

  useEffect(() => {
    if (searchBy === "searchByModelId") {
      if (!categoryFilter || !brandFilter || !modelNumberFilter) {
        setShowIDUConfigDataList(false);
      }
    } else if (searchBy === "searchByConfigId") {
      if (!configIdFilter) {
        setShowIDUConfigDataList(false);
      }
    } else {
      setShowIDUConfigDataList(false);
    }
  }, [searchBy, categoryFilter, brandFilter, modelNumberFilter, configIdFilter]);

  useEffect(() => {
    if (searchBy === "searchByModelId") {
      if (!categoryFilter || !brandFilter || !modelNumberFilter) {
        return;
      }
    } else if (searchBy === "searchByConfigId") {
      if (!configIdFilter) {
        return;
      }
    } else {
      return;
    }

    getIDUConfigFilterData();
  }, [updateFlag]);

  useEffect(() => {
    setShowIDUConfigDataList(false);
  }, [searchBy]);

  const filterIDUConfigData = () => {
    if (searchBy === "searchByModelId") {
      if (!categoryFilter) {
        setAlert("Category Cannot be Empty", "danger");
        return;
      }
      if (!brandFilter) {
        setAlert("Brand Cannot be Empty", "danger");
        return;
      }
      if (!modelNumberFilter) {
        setAlert("Model ID Cannot be Empty", "danger");
        return;
      }
    } else if (searchBy === "searchByConfigId") {
      if (!configIdFilter) {
        setAlert("Config ID Cannot be Empty", "danger");
        return;
      }
    } else {
      setAlert("Invalid Filter", "danger");
      return;
    }

    setUpdateFlag(Math.random());
  };

  const resetFilters = () => {
    setCategoryFilter("");
    setBrandFilter("");
    setModelNumberFilter("");
    setConfigIdFilter("");
    setUpdateFlag(Math.random());
    setShowIDUConfigDataList(false);
  };

  const downloadFile = (fileData) => {
    const params = {
      fileData,
      fileName: "config.bin",
    };

    downloadIDUConfigFile(params);
  };

  const editIDUConfig = (iduConfigVersion, iduConfigId, modelId) => {
    if (searchBy === "searchByConfigId") {
      setIDUConfigDataForEdit({
        iduConfigVersion,
        configId: iduConfigId,
        updateIDUConfigBy: "updateConfigByConfigId",
      });
      return;
    }

    if (searchBy === "searchByModelId") {
      let parsedModel = {};
      try {
        parsedModel = modelNumberFilter ? JSON.parse(modelNumberFilter) : {};
      } catch {
        parsedModel = {};
      }

      setIDUConfigDataForEdit({
        categoryName: categoryFilter,
        brandName: brandFilter,
        model: [{ modelId, modelNumber: parsedModel?.modelNumber || "" }],
        iduConfigVersion,
        configId: iduConfigId,
        updateIDUConfigBy: "updateConfigByModelId",
      });
    }
  };

  const getIDUConfigFilterData = () => {
    setIsIDUConfigDataLoading(true);

    const filteredParams = {};
    if (searchBy === "searchByConfigId") {
      if (configIdFilter) {
        filteredParams.configId = configIdFilter;
      }
    } else if (modelNumberFilter) {
      try {
        filteredParams.modelId = JSON.parse(modelNumberFilter).modelId;
      } catch {
        filteredParams.modelId = "";
      }
    }

    getIDUConfigData(filteredParams)
      .then((res) => {
        setIsIDUConfigDataLoading(false);
        setShowIDUConfigDataList(true);
        setIDUConfigData(res || []);
      })
      .catch(() => {
        setIsIDUConfigDataLoading(false);
        setShowIDUConfigDataList(false);
        setAlert("Failed to Get IDU EEPROM Config", "danger");
      });
  };

  const handleEditByConfigId = () => {
    setIDUConfigDataForEdit({
      iduConfigVersion:
        iduConfigData && iduConfigData.length > 0
          ? iduConfigData[0].iduConfigVersion
          : "",
      configId: configIdFilter,
      updateIDUConfigBy: "updateConfigByConfigId",
    });
  };

  const tableColumns = useMemo(() => {
    const columns = [
      {
        key: "modelId",
        label: TABLE_HEADERS.modelId,
        minWidth: 140,
      },
      {
        key: "configId",
        label: TABLE_HEADERS.configId,
        minWidth: 140,
      },
      {
        key: "iduConfigVersion",
        label: TABLE_HEADERS.iduConfigVersion,
        minWidth: 140,
      },
      {
        key: "iduConfigFileObjectBin",
        label: TABLE_HEADERS.iduConfigFileObjectBin,
        minWidth: 120,
        align: "left",
        render: (value) => (
          <Box
            component="span"
            sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
            onClick={() => downloadFile(value)}
          >
            <CloudDownload sx={{ color: "black", fontSize: 20 }} />
          </Box>
        ),
      },
    ];

    if (searchBy === "searchByModelId") {
      columns.push({
        key: "editIDUConfig",
        label: TABLE_HEADERS.editIDUConfig,
        minWidth: 100,
        align: "left",
        render: (_, row) => (
          <Box
            component="span"
            sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
            onClick={() =>
              editIDUConfig(row.iduConfigVersion, row.configId, row.modelId)
            }
          >
            <EditIcon sx={{ color: "black", fontSize: 20 }} />
          </Box>
        ),
      });
    }

    return columns;
  }, [searchBy, iduConfigData]);

  const tableData = useMemo(
    () =>
      (iduConfigData || []).map((element, index) => ({
        ...element,
        rowId: `${element?.configId || "cfg"}-${element?.modelId || "mdl"}-${index}`,
      })),
    [iduConfigData]
  );

  return (
    <Fragment>
      <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, mb: 2 }}>
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <LabelText sx={{ fontWeight: 600, flexShrink: 0 }}>Search By:</LabelText>
          <AppRadioGroup
            row
            fullWidth={false}
            name="searchBy"
            value={searchBy}
            onChange={(event) => setSearchBy(event.target.value)}
            formControlSx={{ m: 0, p: 0, mt: 0.5 }}
            options={[
              { label: "Model ID", value: "searchByModelId" },
              { label: "Config ID", value: "searchByConfigId" },
            ]}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: searchBy === "searchByModelId" ? "1fr 1fr 1fr auto" : "minmax(0, 360px) auto",
            },
            gap: 2,
            alignItems: "center",
          }}
        >
          {searchBy === "searchByModelId" && (
            <FormInput
              type="select"
              size="small"
              fullWidth
              label="Category"
              name="categoryFilter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              options={(allowedDeviceCategories || []).map((element) => ({
                value: element,
                label: element,
              }))}
              MenuProps={selectMenuProps}
              sx={{ minWidth: 0 }}
            />
          )}

          {searchBy === "searchByModelId" && (
            <FormInput
              type="select"
              size="small"
              fullWidth
              label="Brand"
              name="brandFilter"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              options={(allowedCompanyBrand || []).map((element) => ({
                value: element,
                label: element,
              }))}
              MenuProps={selectMenuProps}
              sx={{ minWidth: 0 }}
            />
          )}

          {searchBy === "searchByModelId" && (
            <FormInput
              type="select"
              size="small"
              fullWidth
              label="Model ID"
              name="modelNumberFilter"
              value={modelNumberFilter}
              onChange={(e) => setModelNumberFilter(e.target.value)}
              options={(modelNumberFilterList || []).map((element, idx) => ({
                value: JSON.stringify(element),
                label: element?.modelId || `Model-${idx + 1}`,
              }))}
              MenuProps={selectMenuProps}
              sx={{ minWidth: 0 }}
            />
          )}

          {searchBy === "searchByConfigId" && (
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Config ID"
              name="configIdFilter"
              value={configIdFilter}
              onChange={(e) => setConfigIdFilter(e.target.value)}
              placeholder="Enter Config ID"
              sx={{ minWidth: 0 }}
            />
          )}

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "flex-start" }}>
            <AppButton variant="primary" size="medium" onClick={filterIDUConfigData}>
              Filter
            </AppButton>
            <AppButton variant="ghost" size="medium" onClick={resetFilters}>
              Reset
            </AppButton>
          </Box>
        </Box>
      </Paper>

      {searchBy === "searchByConfigId" && showIDUConfigDataList && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <AppButton variant="primary" size="medium" onClick={handleEditByConfigId}>
            Edit Configuration
          </AppButton>
        </Box>
      )}

      {isIDUConfigDataLoading ? (
        <Box sx={{ display: "grid", placeItems: "center", height: "20vh" }}>
          <CircularProgress size={48} sx={{ color: colors?.primary?.main || "#AFB42B" }} />
        </Box>
      ) : (
        showIDUConfigDataList && (
          <Paper sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
            <DataTable
              columns={tableColumns}
              data={tableData}
              rowKey="rowId"
              minWidth="100%"
              maxHeight={{ xs: "none", md: "58vh" }}
              stickyHeader
              striped={false}
              hoverEffect={true}
              tableContainerSx={{ boxShadow: "none", borderRadius: 0 }}
              msgForDisplay="No Data found"
            />
          </Paper>
        )
      )}
    </Fragment>
  );
};

const UploadIDUEEPROMConfig = ({
  allowedDeviceCategories,
  allowedCompanyBrand,
  iduConfigStore: { uploadIDUConfigInProgress, uploadIDUConfigSuccess },
  uploadedIDUConfigData,
  editIDUConfig,
  uploadIDUConfig,
  updateIDUConfigByModelId,
  updateIDUConfigByConfigId,
  cancelEditIDUConfig,
  setAlert,
}) => {
  const [uploadedCategoryName, setUploadedCategoryName] = useState(
    uploadedIDUConfigData.categoryName || ""
  );
  const [uploadedBrandName, setUploadedBrandName] = useState(
    uploadedIDUConfigData.brandName || ""
  );
  const [modelNumbersList, setModelumbersList] = useState([]);
  const [uploadedModelNumbers, setUploadedModelNumbers] = useState(
    uploadedIDUConfigData.model || []
  );
  const [uploadedIDUConfigVersion, setUploadedIDUConfigVersion] = useState(
    uploadedIDUConfigData.configversion || ""
  );
  const [uploadedIDUConfigImage, setUploadedIDUConfigImage] = useState("");
  const [uploadedIDUConfigId, setUploadedIDUConfigId] = useState(
    uploadedIDUConfigData.configId || ""
  );
  const [updateIDUConfigBy, setUpdateIDUConfigBy] = useState(
    uploadedIDUConfigData.updateIDUConfigBy || "updateConfigByModelId"
  );

  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || "#AFB42B";
  const focusOutlineSx = {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid rgba(0, 0, 0, 0.42) !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${primaryColor} !important`,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: `${primaryColor} !important`,
    },
  };

  const allModelNumbersSelected =
    modelNumbersList.length > 0 && modelNumbersList.length === uploadedModelNumbers.length;
  const iduConfigImageInput = useRef();
  const filter = createFilterOptions();

  useEffect(() => {
    if (!uploadIDUConfigInProgress && uploadIDUConfigSuccess) {
      setUploadedCategoryName("");
      setUploadedBrandName("");
      setUploadedModelNumbers([]);
      setUploadedIDUConfigVersion("");
      setUploadedIDUConfigImage("");
      setUploadedIDUConfigId("");
      setUpdateIDUConfigBy("");

      if (iduConfigImageInput.current) {
        iduConfigImageInput.current.value = "";
      }

      if (editIDUConfig) {
        cancelEditIDUConfig();
      }
    }
  }, [uploadIDUConfigInProgress, uploadIDUConfigSuccess]);

  useEffect(() => {
    if (!uploadedCategoryName || !uploadedBrandName) {
      return;
    }

    getDeviceModelNumbersList(uploadedBrandName, uploadedCategoryName)
      .then((data) => {
        setModelumbersList(data || []);
        if (!editIDUConfig) {
          setUploadedModelNumbers([]);
        }
      })
      .catch(() => {
        setModelumbersList([]);
        setUploadedModelNumbers([]);
      });
  }, [uploadedBrandName, uploadedCategoryName]);

  useEffect(() => {
    if (editIDUConfig && uploadedIDUConfigData && Object.keys(uploadedIDUConfigData).length !== 0) {
      setUploadedCategoryName(uploadedIDUConfigData.categoryName || "");
      setUploadedBrandName(uploadedIDUConfigData.brandName || "");
      setUploadedIDUConfigVersion(uploadedIDUConfigData.iduConfigVersion || "");
      setUploadedModelNumbers(uploadedIDUConfigData.model || []);
      setUploadedIDUConfigId(uploadedIDUConfigData.configId || "");
      setUpdateIDUConfigBy(uploadedIDUConfigData.updateIDUConfigBy || "updateConfigByModelId");
      setUploadedIDUConfigImage("");
    } else {
      setUploadedCategoryName("");
      setUploadedBrandName("");
      setUploadedIDUConfigVersion("");
      setUploadedModelNumbers([]);
      setUploadedIDUConfigId("");
      setUpdateIDUConfigBy("");
      setUploadedIDUConfigImage("");
    }

    if (iduConfigImageInput.current) {
      iduConfigImageInput.current.value = "";
    }

    if (uploadedIDUConfigData?.updateIDUConfigBy === "updateConfigByConfigId") {
      getDeviceModelIdsUsingConfigId(uploadedIDUConfigData.configId)
        .then((res) => {
          setUploadedModelNumbers(
            (res || []).map((modelId) => ({ modelId, modelNumber: "" }))
          );
        })
        .catch(() => {
          // No-op: keep existing model list on edit-by-config-id failure.
        });
    }
  }, [editIDUConfig, uploadedIDUConfigData]);

  const handleUploadModelNumbers = (event, selectedOptions, reason, details) => {
    const selectedOption = details?.option;
    const cleanedOptions = selectedOptions.filter((option) => option !== "Select All");

    if (selectedOption === "Select All") {
      if (!allModelNumbersSelected) {
        setUploadedModelNumbers([...modelNumbersList]);
      } else {
        setUploadedModelNumbers([]);
      }
      return;
    }

    if (reason === "clear") {
      setUploadedModelNumbers([]);
      return;
    }

    setUploadedModelNumbers(cleanedOptions);
  };

  const handleUploadIDUConfig = () => {
    if (!editIDUConfig || (editIDUConfig && updateIDUConfigBy === "updateConfigByModelId")) {
      if (!uploadedCategoryName) {
        setAlert("Category Name Cannot be Empty", "danger");
        return;
      }
      if (!uploadedBrandName) {
        setAlert("Brand Name Cannot be Empty", "danger");
        return;
      }
      if (!uploadedModelNumbers || uploadedModelNumbers.length === 0) {
        setAlert("Model IDs Cannot be Empty", "danger");
        return;
      }
    }

    if (
      !uploadedIDUConfigVersion ||
      uploadedIDUConfigVersion.match("^[0-9]{1,2}\\.[0-9]{1,2}$") === null
    ) {
      setAlert("Invalid IDU Config Version", "danger");
      return;
    }
    if (
      !uploadedIDUConfigImage ||
      !uploadedIDUConfigImage.name ||
      !(uploadedIDUConfigImage.name.endsWith(".bin") || uploadedIDUConfigImage.name.endsWith(".BIN"))
    ) {
      setAlert("Invalid IDU Config Image", "danger");
      return;
    }

    if (editIDUConfig) {
      if (updateIDUConfigBy === "updateConfigByModelId") {
        const modelId = uploadedModelNumbers.map((obj) => obj.modelId).join(",") || "";
        const formData = new FormData();
        formData.append("iduConfigVersion", uploadedIDUConfigVersion);
        formData.append("iduConfigFile", uploadedIDUConfigImage);
        updateIDUConfigByModelId(modelId, formData);
      } else if (updateIDUConfigBy === "updateConfigByConfigId") {
        const formData = new FormData();
        formData.append("iduConfigVersion", uploadedIDUConfigVersion);
        formData.append("iduConfigFile", uploadedIDUConfigImage);
        updateIDUConfigByConfigId(uploadedIDUConfigId, formData);
      }
      return;
    }

    const formData = new FormData();
    formData.append("category", uploadedCategoryName);
    formData.append(
      "modelId",
      uploadedModelNumbers.map((obj) => obj.modelId).join(",") || ""
    );
    formData.append("iduConfigVersion", uploadedIDUConfigVersion);
    formData.append("iduConfigFile", uploadedIDUConfigImage);
    uploadIDUConfig(formData);
  };

  const handleIDUConfigUploadReset = () => {
    setUploadedCategoryName("");
    setUploadedBrandName("");
    setUploadedModelNumbers([]);
    setUploadedIDUConfigVersion("");
    setUploadedIDUConfigImage("");
    setUploadedIDUConfigId("");
    setUpdateIDUConfigBy("updateConfigByModelId");

    if (iduConfigImageInput.current) {
      iduConfigImageInput.current.value = "";
    }
  };

  const renderModelOption = (props, option, { selected }) => {
    const isSelectAll = option === "Select All";
    const checked = isSelectAll ? allModelNumbersSelected : selected;

    return (
      <li {...props}>
        <AppCheckbox
          color="primary"
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          checkboxSx={{ mr: 1 }}
          checked={checked}
        />
        {isSelectAll ? "Select All" : option?.modelId || ""}
      </li>
    );
  };

  return (
    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
      {(editIDUConfig === false ||
        (editIDUConfig === true && updateIDUConfigBy === "updateConfigByModelId")) && (
        <Fragment>
          <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
            <Grid2 size={{ xs: 12, md: 2 }}>
              <LabelText sx={{ fontWeight: 600 }}>Category:</LabelText>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
              <FormInput
                type="select"
                size="small"
                fullWidth
                label="Select Category"
                value={uploadedCategoryName}
                onChange={(e) => setUploadedCategoryName(e.target.value)}
                options={(allowedDeviceCategories || []).map((categoryName) => ({
                  value: categoryName,
                  label: categoryName,
                }))}
                MenuProps={selectMenuProps}
                sx={formControlSx}
                disabled={editIDUConfig}
              />
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
            <Grid2 size={{ xs: 12, md: 2 }}>
              <LabelText sx={{ fontWeight: 600 }}>Brand:</LabelText>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
              <FormInput
                type="select"
                size="small"
                fullWidth
                label="Select Brand"
                value={uploadedBrandName}
                onChange={(e) => setUploadedBrandName(e.target.value)}
                options={(allowedCompanyBrand || []).map((brandName) => ({
                  value: brandName,
                  label: brandName,
                }))}
                MenuProps={selectMenuProps}
                sx={formControlSx}
                disabled={editIDUConfig}
              />
            </Grid2>
          </Grid2>
        </Fragment>
      )}

      {editIDUConfig === true && updateIDUConfigBy === "updateConfigByConfigId" && (
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Config ID:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Config ID"
              value={uploadedIDUConfigId}
              onChange={(e) => setUploadedIDUConfigId(e.target.value)}
              disabled
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>
      )}

      <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600 }}>Model ID:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <Box sx={formControlSx}>
            <Autocomplete
              multiple
              fullWidth
              size="small"
              limitTags={10}
              options={modelNumbersList}
              value={uploadedModelNumbers}
              disableCloseOnSelect
              getOptionLabel={(option) => {
                if (option == null) return "";
                if (typeof option === "string") return option;
                return option.modelId != null ? String(option.modelId) : "";
              }}
              renderOption={renderModelOption}
              onChange={handleUploadModelNumbers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Model IDs"
                  placeholder={uploadedModelNumbers?.length ? "" : "Search"}
                  fullWidth
                  sx={{
                    ...focusOutlineSx,
                    "& .MuiOutlinedInput-root": {
                      height: "auto",
                      minHeight: 45,
                      alignItems: "center",
                          paddingTop: Boolean(uploadedModelNumbers?.length)
                            ? "4px !important"
                            : "0 !important",
                      paddingBottom: "0 !important",
                    },
                    "& .MuiAutocomplete-inputRoot": {
                      minHeight: 45,
                      alignItems: "center",
                      flexWrap: "wrap",
                          padding: Boolean(uploadedModelNumbers?.length)
                            ? "4px 39px 0 14px !important"
                            : "0 39px 0 14px !important",
                    },
                    "& .MuiAutocomplete-tag": {
                      margin: "3px !important",
                      maxWidth: "calc(100% - 6px)",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "rgba(0, 0, 0, 0.6)",
                      opacity: 1,
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      top: "50%",
                      transform: "translateY(-50%)",
                    },
                    "& .MuiInputBase-input": {
                      borderBottom: "none !important",
                      boxShadow: "none !important",
                      margin: "0 !important",
                      padding: "0 !important",
                    },
                    "& .MuiInputBase-input:focus": {
                      borderBottom: "none !important",
                      boxShadow: "none !important",
                    },
                  }}
                />
              )}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (!uploadedCategoryName || !uploadedBrandName) {
                  return [];
                }
                return ["Select All", ...filtered];
              }}
              isOptionEqualToValue={(option, value) => {
                if (option === value) return true;
                if (option?.modelId != null && value?.modelId != null) {
                  return option.modelId === value.modelId;
                }
                return false;
              }}
              ListboxProps={{ style: { maxHeight: 250 } }}
              disabled={editIDUConfig}
              style={{ width: "100%", minWidth: 0 }}
            />
          </Box>
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600 }}>Config Version:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <FormInput
            type="text"
            size="small"
            fullWidth
            label="Enter IDU Config Version"
            value={uploadedIDUConfigVersion}
            onChange={(e) => setUploadedIDUConfigVersion(e.target.value)}
            sx={formControlSx}
          />
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <Grid2 size={{ xs: 12, md: 2 }}>
          <LabelText sx={{ fontWeight: 600 }}>IDU Config Image:</LabelText>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
          <Box sx={formControlSx}>
            <input
              placeholder="Upload IDU Config File"
              type="file"
              style={{ width: "100%", boxSizing: "border-box", padding: 8, border: "1px solid rgba(0,0,0,0.42)", borderRadius: 4 }}
              name={uploadedIDUConfigImage?.name ?? uploadedIDUConfigImage}
              onChange={(e) => setUploadedIDUConfigImage(e.target.files[0])}
              ref={iduConfigImageInput}
            />
          </Box>
        </Grid2>
      </Grid2>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <AppButton
          variant="primary"
          size="medium"
          onClick={handleUploadIDUConfig}
          disabled={uploadIDUConfigInProgress}
          loading={uploadIDUConfigInProgress}
        >
          Upload
        </AppButton>

        {editIDUConfig ? (
          <AppButton variant="ghost" size="medium" onClick={cancelEditIDUConfig}>
            Cancel
          </AppButton>
        ) : (
          <AppButton variant="ghost" size="medium" onClick={handleIDUConfigUploadReset}>
            Reset
          </AppButton>
        )}
      </Box>
    </Paper>
  );
};

const MQTTTriggerIDUEEPROMConfig = ({
  iduConfigStore: { iduConfigMQTTTriggerInProgress, iduConfigMQTTTriggerSuccess },
  sendIDUConfigMQTTTrigger,
  setAlert,
}) => {
  const [uploadedDeviceId, setUploadedDeviceId] = useState("");

  useEffect(() => {
    if (!iduConfigMQTTTriggerInProgress && iduConfigMQTTTriggerSuccess) {
      setUploadedDeviceId("");
    }
  }, [iduConfigMQTTTriggerInProgress, iduConfigMQTTTriggerSuccess]);

  const handleIDUConfigMQTTTrigger = () => {
    if (!uploadedDeviceId) {
      setAlert("Invalid Device Id", "danger");
      return;
    }

    sendIDUConfigMQTTTrigger(uploadedDeviceId);
  };

  return (
    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <LabelText sx={{ fontWeight: 600, flexShrink: 0 }}>Device ID:</LabelText>
        <Box sx={formControlSx}>
          <FormInput
            type="text"
            size="small"
            fullWidth
            label="Enter Device ID"
            value={uploadedDeviceId}
            onChange={(e) => setUploadedDeviceId(e.target.value)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <AppButton
          variant="primary"
          size="medium"
          onClick={handleIDUConfigMQTTTrigger}
          disabled={iduConfigMQTTTriggerInProgress}
          loading={iduConfigMQTTTriggerInProgress}
        >
          Send
        </AppButton>
        <AppButton variant="ghost" size="medium" onClick={() => setUploadedDeviceId("")}>
          Reset
        </AppButton>
      </Box>
    </Paper>
  );
};

const mapStateToProps = (state) => ({
  allowedDeviceCategories: state.auth.allowedDeviceCategories,
  allowedCompanyBrand: state.auth.allowCompanyBrand,
  iduConfigStore: state.iduConfig,
});

const mapDispatchToProps = (dispatch) => ({
  setAlert: (message, status) => dispatch(setAlert(message, status)),
  uploadIDUConfig: (formData) => dispatch(uploadIDUConfig(formData)),
  updateIDUConfigByModelId: (modelId, formData) =>
    dispatch(updateIDUConfigByModelId(modelId, formData)),
  updateIDUConfigByConfigId: (configId, formData) =>
    dispatch(updateIDUConfigByConfigId(configId, formData)),
  downloadIDUConfigFile: (params) => dispatch(downloadIDUConfigFile(params)),
  sendIDUConfigMQTTTrigger: (params) => dispatch(sendIDUConfigMQTTTrigger(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IDUEEPROMConfig);
